import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import api, { API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const PERIODS = { daily: "Günlük", weekly: "Haftalık", monthly: "Aylık", yearly: "Yıllık" };

export default function Reports() {
  const [period, setPeriod] = useState("monthly");
  const [department, setDepartment] = useState("all");
  const [data, setData] = useState({ summary: [], rows: [], from: "", to: "" });
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState("all");

  useEffect(() => {
    api.get("/employees").then(({ data }) => setEmployees(data));
  }, []);

  const load = async (p = period) => {
    const params = { period: p };
    if (department !== "all") params.department = department;
    if (employeeId !== "all") params.employee_id = employeeId;
    const { data } = await api.get("/reports", { params });
    setData(data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period, department, employeeId]);

  const downloadCSV = async () => {
    const params = new URLSearchParams({ period, format: "csv" });
    if (department !== "all") params.set("department", department);
    if (employeeId !== "all") params.set("employee_id", employeeId);
    window.open(`${API_BASE}/reports?${params}`, "_blank");
  };

  const departments = Array.from(new Set(employees.map((e) => e.department).filter(Boolean)));

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Raporlar</h1>
          <p className="text-muted-foreground text-sm mt-1">{data.from} — {data.to}</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={downloadCSV} variant="outline" className="gap-2" data-testid="download-csv">
            <Download className="w-4 h-4" /> CSV
          </Button>
          <Button onClick={() => window.print()} className="gap-2" data-testid="print-report">
            <Download className="w-4 h-4" /> PDF (Yazdır)
          </Button>
        </div>
      </div>

      <div className="glass-card rounded-xl p-4 flex flex-col lg:flex-row lg:flex-wrap gap-3 lg:items-end">
        <div className="flex-1 min-w-[200px]">
          <Label className="mb-1.5 block">Dönem</Label>
          <Tabs value={period} onValueChange={setPeriod}>
            <TabsList className="grid grid-cols-4 w-full">
              {Object.entries(PERIODS).map(([k, v]) => (
                <TabsTrigger key={k} value={k} data-testid={`period-${k}`}>{v}</TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
        <div className="grid grid-cols-2 lg:flex gap-3">
          <div>
            <Label className="mb-1.5 block">Departman</Label>
            <Select value={department} onValueChange={setDepartment}>
              <SelectTrigger className="w-full lg:w-48"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                {departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="mb-1.5 block">Personel</Label>
            <Select value={employeeId} onValueChange={setEmployeeId}>
              <SelectTrigger className="w-full lg:w-56"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="glass-card rounded-xl overflow-hidden">
        <div className="p-4 border-b border-border/60 font-display font-semibold tracking-tight">Personel Özeti</div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Personel No</TableHead>
              <TableHead>Ad Soyad</TableHead>
              <TableHead>Departman</TableHead>
              <TableHead className="text-right">Gün</TableHead>
              <TableHead className="text-right">Geç (dk)</TableHead>
              <TableHead className="text-right">Erken Çıkış (dk)</TableHead>
              <TableHead className="text-right">Fazla Mesai (dk)</TableHead>
              <TableHead className="text-right">Toplam Çalışma (sa)</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.summary.map((s, i) => (
              <TableRow key={i}>
                <TableCell className="font-mono">{s.employee_no}</TableCell>
                <TableCell className="font-medium">{s.name}</TableCell>
                <TableCell>{s.department}</TableCell>
                <TableCell className="text-right font-mono">{s.days}</TableCell>
                <TableCell className="text-right font-mono text-amber-500">{s.late}</TableCell>
                <TableCell className="text-right font-mono">{s.early}</TableCell>
                <TableCell className="text-right font-mono text-blue-500">{s.overtime}</TableCell>
                <TableCell className="text-right font-mono font-semibold">{Math.round((s.worked || 0) / 60 * 10) / 10}</TableCell>
              </TableRow>
            ))}
            {data.summary.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-10">Kayıt yok.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {(data.rows || []).some((r) => r.exit_reason) && (
        <div className="glass-card rounded-xl overflow-hidden" data-testid="early-exits-table">
          <div className="p-4 border-b border-border/60 font-display font-semibold tracking-tight">
            Erken Çıkışlar (Sebep & Onaylayan)
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tarih</TableHead>
                <TableHead>Ad Soyad</TableHead>
                <TableHead>Çıkış Saati</TableHead>
                <TableHead>Erken (dk)</TableHead>
                <TableHead>Sebep</TableHead>
                <TableHead>Onaylayan</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.rows.filter((r) => r.exit_reason).map((r, i) => (
                <TableRow key={i}>
                  <TableCell className="font-mono">{r.date}</TableCell>
                  <TableCell className="font-medium">{r.name}</TableCell>
                  <TableCell className="font-mono text-sm">{r.check_out ? new Date(r.check_out).toLocaleTimeString("tr-TR") : "—"}</TableCell>
                  <TableCell className="font-mono text-amber-500">{r.early_leave_minutes}</TableCell>
                  <TableCell>{r.exit_reason}</TableCell>
                  <TableCell className="text-muted-foreground">{r.approver_name || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
